import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { Toaster } from "sonner";
import { ArrowLeft } from "lucide-react";
import { BrandMark } from "@/components/BrandMark";
import PricingSection from "@/components/PricingSection";
import GoogleAuthButton from "@/components/GoogleAuthButton";
import LandingBottomNav from "@/components/LandingBottomNav";
import ReferralCard from "@/components/ReferralCard";
import SubscriptionCard from "@/components/SubscriptionCard";
import { useAuth } from "@/contexts/AuthContext";

export default function Pricing() {
  const navigate = useNavigate();
  const { user, refreshAuth } = useAuth();
  // Surface ReferralCard + SubscriptionCard for any authed user. ReferralCard
  // self-renders an empty state, SubscriptionCard only shows the active sub
  // for paid users — both are safe to mount unconditionally for an authed
  // user without leaking ghost UI for free users.
  const isAuthed = Boolean(user);
  const plan = (user?.plan || "free").toLowerCase();
  const isPaid = isAuthed && plan !== "free";

  return (
    <div
      className="min-h-screen bg-[#0A0A0F] text-[#F0EDE8] relative pb-16 md:pb-0"
      data-testid="pricing-page"
    >
      <Toaster position="top-center" richColors theme="dark" />

      {/* Header — mirrors Landing for consistency */}
      <header className="border-b border-[#2A2A35] bg-[#0A0A0F]/80 backdrop-blur sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 py-3 sm:py-4 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 sm:gap-4 min-w-0">
            <BrandMark />
          </div>
          <div className="flex items-center gap-1.5 sm:gap-3 shrink-0">
            <Link
              to="/"
              className="text-[10px] sm:text-xs uppercase tracking-[0.14em] sm:tracking-[0.18em] font-bold px-2 sm:px-3 py-1.5 sm:py-2 border border-[#2A2A35] text-[#888] hover:text-[#F0EDE8] hover:border-[#F0EDE8] transition-colors whitespace-nowrap inline-flex items-center gap-1.5"
              data-testid="pricing-back-link"
            >
              <ArrowLeft className="w-3 h-3" />
              <span className="hidden sm:inline">Back to home</span>
              <span className="sm:hidden">Back</span>
            </Link>
            <GoogleAuthButton target="/" />
            <button
              type="button"
              className="btn-saffron text-xs sm:text-sm px-2.5 sm:px-4 py-2 whitespace-nowrap"
              onClick={() => navigate("/")}
              data-testid="pricing-cta-btn"
            >
              <span className="sm:hidden">Free →</span>
              <span className="hidden sm:inline">Analyse Free →</span>
            </button>
          </div>
        </div>
      </header>

      {/* Page hero — sets expectations */}
      <section className="max-w-4xl mx-auto px-6 pt-12 md:pt-16 pb-2 text-center">
        <div className="inline-flex items-center gap-2 px-3 py-1 border border-[#FF6B00] text-[#FF6B00] text-[11px] uppercase tracking-[0.22em] font-bold">
          Plans &amp; pricing
        </div>
        <h1
          className="font-display uppercase mt-5 text-[clamp(36px,5vw,56px)] leading-[1.05] tracking-tight"
          data-testid="pricing-page-heading"
        >
          Pick the plan that <span className="text-[#FF6B00]">grows with you.</span>
        </h1>
        <p className="mt-4 text-[#888] text-base md:text-lg max-w-2xl mx-auto">
          Start free. Upgrade only when CartIQ 360 has shown you enough money to make it
          worth it. Cancel anytime.
        </p>
      </section>

      {/* The full pricing section — same component used everywhere */}
      <PricingSection
        onFreeCtaClick={() => navigate("/")}
      />

      {/* Authed-only: Refer & Earn + Subscription management.
          Moved here from the Dashboard Budget tab so /pricing is the single
          place users go to manage everything money-related. */}
      {isAuthed && (
        <section
          className="max-w-4xl mx-auto px-4 md:px-6 mt-4 md:mt-8 space-y-4 md:space-y-6"
          data-testid="pricing-account-section"
        >
          <ReferralCard />
          {isPaid && <SubscriptionCard onCancelled={refreshAuth} />}
        </section>
      )}

      {/* Footer */}
      <footer className="border-t border-[#2A2A35] mt-10" data-testid="pricing-footer">
        <div className="max-w-7xl mx-auto px-6 py-8 flex flex-col md:flex-row justify-between gap-4 text-xs text-[#555566] uppercase tracking-[0.18em]">
          <div>CartIQ 360 · Solutionist</div>
          <div className="flex flex-wrap items-center gap-5">
            <Link
              to="/"
              className="hover:text-[#F0EDE8] transition-colors"
              data-testid="pricing-footer-home-link"
            >
              Home
            </Link>
            <Link
              to="/terms"
              className="hover:text-[#F0EDE8] transition-colors"
              data-testid="pricing-footer-terms-link"
            >
              Terms &amp; Conditions
            </Link>
            <Link
              to="/privacy"
              className="hover:text-[#F0EDE8] transition-colors"
              data-testid="pricing-footer-privacy-link"
            >
              Privacy Policy
            </Link>
            <a
              href="mailto:support@solutionist.co.in"
              className="hover:text-[#F0EDE8] transition-colors"
              data-testid="pricing-footer-contact-link"
            >
              support@solutionist.co.in
            </a>
          </div>
          <div className="hidden md:block">Born in India · Built for the World.</div>
        </div>
      </footer>

      <LandingBottomNav />
    </div>
  );
}
